import { LOCATIONS, VIDEO_TYPES, type LocationSlug } from "@/lib/site";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import stagLogo from "@/assets/stag-logo.png";
import { Mail, Linkedin, MessageCircle, Instagram, Facebook, Phone, Home, TrendingUp, Clapperboard, BarChart3, Wrench, Info, Building2, FileText, MessageSquareQuote, GraduationCap, Package, CalendarDays, Plane, Share2, Globe, type LucideIcon } from "lucide-react";
import { AwardFooter } from "@/components/site/AwardRecognition";
import { useScrollHighlight } from "@/hooks/useScrollHighlight";

const ColumnHeading = ({ children }: { children: React.ReactNode }) => (
  <p className="flex items-center justify-center gap-3 font-semibold text-primary-foreground md:justify-start">
    <span className="h-0.5 w-6 rounded-full bg-primary-foreground/40" aria-hidden="true" />
    {children}
    <span className="h-0.5 w-6 rounded-full bg-primary-foreground/40 md:hidden" aria-hidden="true" />
  </p>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === "/";
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const { isTouchDevice } = useScrollHighlight();

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isTouchDevice) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    if (isHomePage) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      e.preventDefault();
      navigate("/");
    }
  };

  const handleNavClick = (e: React.MouseEvent, href: string) => {
    if (!isHomePage && href.startsWith("#")) {
      e.preventDefault();
      navigate("/" + href);
    }
  };

  const footerLinks = {
    company: [
      { name: "Home", href: "#", Icon: Home },
      { name: "Lead gen", href: "/video-growth-engine", Icon: TrendingUp },
      { name: "Video production", href: "/commercial-video", Icon: Clapperboard },
      { name: "Our results", href: "/our-results", Icon: BarChart3 },
      { name: "Other services", href: "/other-services", Icon: Wrench },
      { name: "About", href: "/about", Icon: Info },
      { name: "Contact", href: "/#contact", Icon: Mail },
    ],
    legal: [
      { name: "Privacy Policy", href: "/privacy-policy" },
      { name: "Cookie Policy", href: "/cookie-policy" },
      { name: "Accessibility Statement", href: "/accessibility-statement" },
      { name: "Terms", href: "/terms" },
    ],
  };

  const videoTypeIcons: Record<string, LucideIcon> = {
    "company-profile-video": Building2,
    "case-study-video": FileText,
    "testimonial-video": MessageSquareQuote,
    "training-video": GraduationCap,
    "product-video": Package,
    "event-videography": CalendarDays,
    "drone-videography": Plane,
    "social-media-video": Share2,
    "website-video": Globe,
  };

  const socials = [
    { label: "Instagram", href: "https://www.instagram.com/stagmarketinggroup/", Icon: Instagram },
    { label: "Facebook", href: "https://www.facebook.com/stagmarketinggroup/", Icon: Facebook },
    { label: "LinkedIn", href: "https://www.linkedin.com/company/stag-marketing-group/", Icon: Linkedin },
  ];

  return (
    <footer className="brand-hero relative overflow-hidden">
      {/* Oversized watermark logo */}
      <img
        src={stagLogo}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-16 right-0 z-0 h-80 w-auto select-none opacity-[0.07] brightness-0 invert lg:h-96"
        loading="lazy"
        decoding="async"
      />
      {/* Call to action band */}
      <div className="container relative z-10 mx-auto px-6 pt-14">
        <div className="on-light flex flex-col items-center justify-between gap-6 rounded-3xl bg-primary-foreground p-8 text-center shadow-2xl md:flex-row md:text-left lg:p-10">
          <div>
            <p className="text-2xl font-bold text-primary">Ready to win more work with video?</p>
            <p className="mt-2 text-base text-primary">Tell us what you need. We will come back with ideas, not a sales script.</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <a
              href="tel:+447403682194"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground transition-transform hover:scale-[1.03]"
            >
              <Phone className="h-4 w-4" aria-hidden="true" />
              Call 07403 682194
            </a>
            <a
              href="https://wa.me/447403682194"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-primary px-6 py-3 font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
            >
              <MessageCircle className="h-4 w-4" aria-hidden="true" />
              WhatsApp us
            </a>
          </div>
        </div>
      </div>

      {/* Main footer content */}
      <div className="container relative z-10 mx-auto px-6 py-14">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-[1.1fr_0.8fr_1.05fr_1.35fr] lg:gap-10">
          {/* Brand section */}
          <div className="flex flex-col items-center text-center md:items-start md:text-left">
            <a href={isHomePage ? "#" : "/"} onClick={handleLogoClick} className="block cursor-pointer">
              <div
                className="relative inline-block"
                onMouseMove={handleMouseMove}
                onMouseEnter={() => !isTouchDevice && setIsHovering(true)}
                onMouseLeave={() => !isTouchDevice && setIsHovering(false)}
              >
                <img
                  src={stagLogo}
                  alt="Stag Marketing Group"
                  width={56}
                  height={56}
                  className="relative h-14 w-auto brightness-0 invert"
                  loading="lazy"
                  decoding="async"
                />
                {!isTouchDevice && (
                  <div
                    className="pointer-events-none absolute mix-blend-screen transition-opacity duration-500 ease-in-out"
                    style={{
                      left: `${mousePosition.x}px`,
                      top: `${mousePosition.y}px`,
                      width: "120px",
                      height: "120px",
                      transform: "translate(-50%, -50%)",
                      background:
                        "radial-gradient(circle, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.4) 30%, transparent 60%)",
                      filter: "blur(25px)",
                      opacity: isHovering ? 1 : 0,
                      zIndex: 10,
                    }}
                  />
                )}
              </div>
            </a>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-primary-foreground">
              A video marketing studio. We film, edit and run video that brings in real enquiries for local businesses.
            </p>
            <p className="mt-3 text-sm text-primary-foreground">Based in North Wales and Shropshire</p>
            <div className="mt-5 flex items-center gap-3">
              {socials.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-primary-foreground/30 text-primary-foreground transition-colors hover:bg-primary-foreground hover:text-primary"
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </a>
              ))}
            </div>
            <AwardFooter />
          </div>

          {/* Explore */}
          <nav aria-label="Footer navigation" className="text-center md:text-left">
            <ColumnHeading>Explore</ColumnHeading>
            <ul className="mt-5 flex flex-col items-center space-y-2.5 md:items-start">
              {footerLinks.company.map(({ name, href, Icon }) => (
                <li key={name}>
                  <a
                    href={href}
                    onClick={(e) => handleNavClick(e, href)}
                    className="group inline-flex items-center gap-3 text-base text-primary-foreground"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-foreground/15 transition-colors group-hover:bg-primary-foreground group-hover:text-primary">
                      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                    </span>
                    {name}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Video types */}
          <nav aria-label="Video types" className="text-center md:text-left">
            <ColumnHeading>Video types</ColumnHeading>
            <ul className="mt-5 flex flex-col items-center space-y-2.5 md:items-start">
              {VIDEO_TYPES.map((v) => {
                const Icon = videoTypeIcons[v.slug] ?? Clapperboard;
                return (
                  <li key={v.slug}>
                    <Link
                      to={`/${v.slug}`}
                      className="group inline-flex items-center gap-3 text-base text-primary-foreground"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-foreground/15 transition-colors group-hover:bg-primary-foreground group-hover:text-primary">
                        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                      </span>
                      {v.name}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Contact section */}
          <div className="text-center md:text-left">
            <ColumnHeading>Get in touch</ColumnHeading>
            <ul className="mt-5 flex flex-col items-center space-y-3 md:items-start">
              {[
                { label: "07403 682194", href: "tel:+447403682194", Icon: Phone },
                { label: "WhatsApp us", href: "https://wa.me/447403682194", Icon: MessageCircle },
                { label: "@stagmarketinggroup", href: "https://www.instagram.com/stagmarketinggroup/", Icon: Instagram },
                { label: "hello@stagmarketinggroup.co.uk", href: "mailto:hello@stagmarketinggroup.co.uk", Icon: Mail },
              ].map(({ label, href, Icon }) => (
                <li key={label} className="min-w-0 max-w-full">
                  <a
                    href={href}
                    target={href.startsWith("http") ? "_blank" : undefined}
                    rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                    className="group inline-flex max-w-full items-center gap-3 text-base text-primary-foreground"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-foreground/15 transition-colors group-hover:bg-primary-foreground group-hover:text-primary">
                      <Icon className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 text-left [overflow-wrap:anywhere]">{label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Areas we cover */}
      <div className="relative z-10 border-t border-primary-foreground/15">
        <nav aria-label="Areas we cover" className="container mx-auto px-6 py-8 text-center md:text-left">
          <ColumnHeading>Areas we cover</ColumnHeading>
          <ul className="mt-4 flex flex-wrap justify-center gap-2 md:justify-start">
            {(Object.keys(LOCATIONS) as LocationSlug[]).map((s) => (
              <li key={s}>
                <Link
                  to={`/${s}`}
                  className="inline-block rounded-full border border-primary-foreground/25 px-3.5 py-1.5 text-[0.8125rem] text-primary-foreground transition-colors hover:bg-primary-foreground hover:text-primary"
                >
                  Videographer {LOCATIONS[s].town}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      {/* Footer bottom - Copyright */}
      <div className="relative z-10 border-t border-primary-foreground/15">
        <div className="container mx-auto px-6 py-4">
          <div className="flex flex-col items-center justify-between gap-3 md:flex-row">
            <p className="text-center text-[0.8125rem] text-primary-foreground md:text-left">
              <span className="block sm:inline">© {currentYear} Stag Marketing Group Ltd.</span>{" "}
              <a
                href="https://find-and-update.company-information.service.gov.uk/company/16899790"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-primary-foreground underline-offset-2 hover:underline sm:inline"
              >
                Company No. 16899790
              </a>
            </p>
            <nav aria-label="Legal links" className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
              {footerLinks.legal.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-[0.8125rem] text-primary-foreground transition-colors hover:underline"
                >
                  {link.name}
                </a>
              ))}
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
